import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { PlusOutlined } from "@ant-design/icons";
import { Table, Button, Select } from "antd";
import { connect } from "react-redux";
import InputElement from "./ReportInputElement";
import {
  BIANNUALLY,
  MONTHLY,
  QUARTERLY,
  WEEKLY,
  YEARLY,
  generateDefaultReportObj,
  getOverrideSolution,
} from "../../utils/report";
import { useGetProjectReportCategories } from "../../hooks";
import { pmsBasedFileMappingCondition } from "../../utils/pms.utils";

const { Option } = Select;

const AdditionalReportsTable = ({
  type,
  fieldArrayProps,
  formRelatedProps,
  isAdminUser,
  projectSolution,
  project,
  hotels,
  availableDocTypes = [],
  selectedPmsLabel,
}) => {
  const { control, watch, reset, resetField, setValue, getValues, errors } =
    formRelatedProps;
  const { fields, remove, append } = fieldArrayProps;
  const defaultFileMappingCondition = pmsBasedFileMappingCondition(selectedPmsLabel);

  const { data: projectReportCategories } = useGetProjectReportCategories(
    project,
    {
      enabled: !!project,
    },
  );

  const tableDataSource = useMemo(() => {
    return fields.filter((field) => field.shedule_type === type);
  }, [fields]);

  const columns = [
    {
      title: "Report Name",
      dataIndex: "ReportName",
      key: "ReportName",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.ReportName`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="ReportName"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "PMS Report Name",
      dataIndex: "PMSReportName",
      width: "250px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.PMSReportName`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="PMSReportName"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Report Frequency",
      dataIndex: "shedule_type",
      width: "200px",
    },
    {
      title: "Report Category",
      dataIndex: "report_category",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.report_category`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={projectReportCategories ?? []}
              errors={errors}
              dataIndex="report_category"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Signature Type",
      dataIndex: "signature_type",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.signature_type`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="signature_type"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Report Order",
      dataIndex: "ReportOrder",
      width: "200px",
      defaultSortOrder: "ascend",
      sorter: (a, b) => {
        return (
          (typeof a.ReportOrder !== "number" ? 99999999 : a.ReportOrder) -
          (typeof b.ReportOrder !== "number" ? 99999999 : b.ReportOrder)
        );
      },
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.ReportOrder`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="ReportOrder"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Signature Mandatory",
      dataIndex: "SignatureMandatory",
      width: "250px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.SignatureMandatory`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="SignatureMandatory"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Smart Scan",
      dataIndex: "smart_scan_type",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.smart_scan_type`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="smart_scan_type"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Generate Redact Files",
      dataIndex: "enable_redaction",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.enable_redaction`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              dataIndex="enable_redaction"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Generate Flat Files",
      dataIndex: "export_flat_files",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.export_flat_files`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              dataIndex="export_flat_files"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Split Reports By Sub Header Type",
      dataIndex: "split_by_sub_header",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.split_by_sub_header`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              dataIndex="split_by_sub_header"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Document Type",
      dataIndex: "document_type",
      width: "300px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.document_type`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              dataIndex="document_type"
              formInputPath={`formData.${record.rowIndex}.`}
              documentTypes={availableDocTypes}
            />
          )}
        />
      ),
    },
    {
      title: "Submission Method",
      dataIndex: "SourceType",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.SourceType`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="SourceType"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "myDocs Report Type",
      dataIndex: "mydocs_report_type",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.mydocs_report_type`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="mydocs_report_type"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    ...([MONTHLY, QUARTERLY, BIANNUALLY, YEARLY].includes(type)
      ? [
          {
            title: "Schedule Type",
            dataIndex: "schedule_trigger_type",
            width: "150px",
            render: (_text, record, index) => (
              <Controller
                control={control}
                name={`formData.${record.rowIndex}.schedule_trigger_type`}
                render={({ field }) => (
                  <InputElement
                    field={field}
                    resetField={resetField}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    reportCategories={[]}
                    errors={errors}
                    dataIndex="schedule_trigger_type"
                    formInputPath={`formData.${record.rowIndex}.`}
                  />
                )}
              />
            ),
          },
        ]
      : []),
    ...([WEEKLY, MONTHLY, QUARTERLY, YEARLY].includes(type)
      ? [
          {
            title: "Schedule",
            dataIndex: "schedule_weekday",
            key: "schedule_weekday",
            width: "150px",
            render: (_text, record, index) => (
              <Controller
                control={control}
                name={`formData.${record.rowIndex}.schedule_weekday`}
                render={({ field }) => (
                  <InputElement
                    field={field}
                    resetField={resetField}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    reportCategories={[]}
                    errors={errors}
                    dataIndex="schedule_weekday"
                    formInputPath={`formData.${record.rowIndex}.`}
                  />
                )}
              />
            ),
          },
        ]
      : []),
    ...([BIANNUALLY].includes(type)
      ? [
          {
            title: "Schedule",
            dataIndex: "schedule_trigger_data",
            key: "schedule_trigger_data",
            width: "150px",
            editable: true,
            render: (_text, record, index) => (
              <Controller
                control={control}
                name={`formData.${record.rowIndex}.schedule_trigger_data`}
                render={({ field }) => (
                  <InputElement
                    field={field}
                    resetField={resetField}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    reportCategories={[]}
                    errors={errors}
                    dataIndex="schedule_trigger_data"
                    formInputPath={`formData.${record.rowIndex}.`}
                  />
                )}
              />
            ),
          },
        ]
      : []),
    ...([MONTHLY, QUARTERLY, YEARLY].includes(type)
      ? [
          {
            title: "Month",
            dataIndex: "schedule_months",
            key: "schedule_months",
            width: "200px",
            render: (_text, record, index) => (
              <Controller
                control={control}
                name={`formData.${record.rowIndex}.schedule_months`}
                render={({ field }) => (
                  <InputElement
                    field={field}
                    resetField={resetField}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    reportCategories={[]}
                    errors={errors}
                    dataIndex="schedule_months"
                    formInputPath={`formData.${record.rowIndex}.`}
                  />
                )}
              />
            ),
          },
        ]
      : []),
    {
      title: "Business Date",
      dataIndex: "business_date",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.business_date`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="business_date"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Subjects",
      dataIndex: "emailSubjects",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.emailSubjects`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="emailSubjects"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "File Names",
      dataIndex: "filenames",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.filenames`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="filenames"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "File Extension",
      dataIndex: "FileExtension",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.FileExtension`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="FileExtension"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "File Mapping Name",
      dataIndex: "FileMappingName",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.FileMappingName`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="FileMappingName"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "QC Ignored Report",
      dataIndex: "QC_IgnoredReport",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.QC_IgnoredReport`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="QC_IgnoredReport"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Compliance",
      dataIndex: "ComplianceEnabled",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.ComplianceEnabled`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="ComplianceEnabled"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Report Name Matching Condition",
      dataIndex: "report_name_matching_condition",
      width: "240px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.report_name_matching_condition`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="report_name_matching_condition"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "File Extension Mapping",
      dataIndex: "file_extension_mapping",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.file_extension_mapping`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="file_extension_mapping"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Activation Date",
      dataIndex: "activation_date",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.activation_date`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="activation_date"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Deactivation Date",
      dataIndex: "deactivation_date",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.deactivation_date`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="deactivation_date"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Signature Activation Date",
      dataIndex: "signature_activation_date",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.signature_activation_date`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="signature_activation_date"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    {
      title: "Signature Deactivation Date",
      dataIndex: "signature_deactivation_date",
      width: "200px",
      render: (_text, record, index) => (
        <Controller
          control={control}
          name={`formData.${record.rowIndex}.signature_deactivation_date`}
          render={({ field }) => (
            <InputElement
              field={field}
              resetField={resetField}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              reportCategories={[]}
              errors={errors}
              dataIndex="signature_deactivation_date"
              formInputPath={`formData.${record.rowIndex}.`}
            />
          )}
        />
      ),
    },
    ...(isAdminUser
      ? [
          {
            title: "Solution",
            dataIndex: "report_type",
            width: "200px",
            render: (_text, record, index) => (
              <Controller
                control={control}
                name={`formData.${record.rowIndex}.report_type`}
                render={({ field }) => (
                  <Select
                    placeholder="Solution"
                    style={{ width: "100%" }}
                    allowClear
                    {...field}
                  >
                    {getOverrideSolution(projectSolution).map((solution) => (
                      <Option key={solution.value} value={solution.value}>
                        {solution.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            ),
          },
        ]
      : []),
    {
      title: "Operation",
      dataIndex: "",
      width: "200px",
      render: (_text, record, index) => (
        <Button onClick={() => remove(record.rowIndex)} type="link">
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      {tableDataSource.length > 0 && (
        <Table
          bordered={false}
          style={{ marginBottom: "8px" }}
          columns={columns}
          dataSource={tableDataSource}
          size="small"
          rowKey="id"
          pagination={false}
          scroll={{ x: 4600 }}
        />
      )}
      <Button
        onClick={() => {
          append({
            ...generateDefaultReportObj(type),
            report_name_matching_condition: defaultFileMappingCondition,
          });
        }}
        icon={<PlusOutlined />}
        type="primary"
      >
        Add Report
      </Button>
    </>
  );
};

AdditionalReportsTable.defaultProps = {
  projectSolution: "",
};

AdditionalReportsTable.propTypes = {
  projectSolution: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    isAdminUser: state.api?.permission?.sm_solution_access,
  };
};

export default connect(mapStateToProps, null)(AdditionalReportsTable);
